import { Loader2 } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { type Anomaly, type AnomalyWeekly, apiWeeklyAnomalies } from './api';
import { formatValue } from './humanize';

/**
 * The owner's week.
 *
 * The full list answers "what was statistically unusual". This answers "what is
 * worth your morning", and it is a different question with a different answer:
 * thirty open cards on sobhy become nine, and the current week becomes one.
 *
 * Two rules do all of it, both on the server (anomaly_owner_view.py):
 *
 *  - a movement smaller than 5% of what this customer does in a day is not
 *    shown, because 485% of a small number is a small number;
 *  - one card per metric per week, because purchases moving on five days
 *    running is one story, not five.
 *
 * This component groups and counts. It does NOT draw the card — `renderItem`
 * hands that back to the page, which already has one that reads an alert's
 * meaning from the server (`readAlert`), opens the detail sheet, and carries
 * "Ask Signals why". A card written fresh here lost all three, and worked out
 * its own arrow from the sign of `delta`, which is the exact mistake that put
 * an up arrow over returns falling to zero.
 *
 * Nothing is deleted and nothing is hidden permanently: "Show every card" is
 * right there, and the numbers behind the reduction are printed rather than
 * left as a silent difference between this page and that one.
 */

const dayMonth = (iso: string) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  });

export default function WeeklyDigest({
  renderItem,
  onShowAll
}: {
  renderItem: (item: Anomaly, others: Anomaly[]) => ReactNode;
  onShowAll: () => void;
}) {
  const [data, setData] = useState<AnomalyWeekly | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiWeeklyAnomalies(8)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 justify-center py-16 text-[13px]"
        style={{ color: 'var(--sig-muted)' }}
      >
        <Loader2 className="size-4 animate-spin" />
        Reading this week…
      </div>
    );
  }

  // A failed request must never be dressed as an all-clear.
  if (error) {
    return (
      <div
        className="px-4 py-3 text-[13px]"
        style={{
          border: '1px solid var(--sig-warn-border)',
          background: 'var(--sig-warn-bg)',
          color: 'var(--sig-warn-text)',
          borderRadius: 11
        }}
      >
        This week could not be loaded, so this page is not saying anything is
        fine — it is saying it does not know. {error}
      </div>
    );
  }

  const weeks = data?.weeks ?? [];
  const shown = weeks.reduce((n, w) => n + w.items.length, 0);

  return (
    <div className="flex flex-col gap-5">
      {shown === 0 ? (
        <div
          className="px-5 py-10 text-center"
          style={{
            border: '1px solid var(--sig-border)',
            borderRadius: 12,
            background: 'var(--sig-panel)'
          }}
        >
          <div
            className="text-[15px] font-semibold"
            style={{ color: 'var(--sig-ink)' }}
          >
            Nothing moved enough to need you
          </div>
          <div
            className="text-[13px] mt-1.5"
            style={{ color: 'var(--sig-muted)' }}
          >
            {data?.floor
              ? `Smaller changes are still being watched — anything under ${formatValue(data.floor)} is in the full list.`
              : 'Smaller changes are still being watched.'}
          </div>
        </div>
      ) : null}

      {weeks.map((week) => (
        <section key={week.week_start} className="flex flex-col gap-3">
          <div
            className="text-[11px] font-bold uppercase"
            style={{ color: 'var(--sig-subtle)', letterSpacing: '.07em' }}
            dir="ltr"
          >
            {dayMonth(week.week_start)} – {dayMonth(week.week_end)}
          </div>
          {week.items.map((item) => (
            <div key={item.id}>{renderItem(item, item.others ?? [])}</div>
          ))}
        </section>
      ))}

      {/* What the reduction cost. The Weekly/All switch at the top of the page
          is how you move between the two; this says what the choice means. */}
      {data && data.hidden_below_floor > 0 ? (
        <button
          type="button"
          onClick={onShowAll}
          className="self-start text-left text-[12.5px]"
          style={{ color: 'var(--sig-muted)' }}
        >
          {data.hidden_below_floor} smaller change
          {data.hidden_below_floor === 1 ? '' : 's'} — under{' '}
          {data.floor ? formatValue(data.floor) : 'the floor'} — are in{' '}
          <span
            className="underline underline-offset-2"
            style={{ color: 'var(--sig-blue-deep)' }}
          >
            All
          </span>
          .
        </button>
      ) : null}
    </div>
  );
}
